import useTestId from '@ori/testid-generator';
import { ErrorCircle, Send, SendFilled } from '@ori-ui/icons';
import type { FC, FormEvent, SyntheticEvent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { NOTIFY_FORM } from './constants';
import { Button, Field, Form, Information, Input, Label, Root, ValidateInfo } from './styles';
import { useAppContext, useTranslations } from '../../hooks';
import { BackInStockNotificationChannel } from '../../models';

export interface NotifyFormProps {
  currentType: string;
  inputData: string;
  productCode: string;
  onChange: (event: FormEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
}

export const NotifyForm: FC<NotifyFormProps> = ({ currentType, inputData, onSubmit, onChange, productCode }) => {
  const { getTestId } = useTestId();
  const { subscribe } = useAppContext();
  const { translations } = useTranslations();
  const isEmail: boolean = BackInStockNotificationChannel.Email === currentType;
  const isCorrectInputData: boolean = isEmail ? z.string().email().safeParse(inputData).success : !!inputData.length;
  const isValid = isCorrectInputData && !!inputData.length;
  const isInvalid = !inputData.length || !isCorrectInputData;

  const label = useMemo(
    () => (isEmail ? translations.enterYourEmail : translations.enterTelephoneNumber),
    [isEmail, translations.enterTelephoneNumber, translations.enterYourEmail],
  );
  const errorLabel = useMemo(
    () => (isEmail ? translations.invalidEmail : translations.invalidTelephoneNumber),
    [isEmail, translations.invalidEmail, translations.invalidTelephoneNumber],
  );

  const onClick = useCallback(
    async (event: SyntheticEvent) => {
      event.preventDefault();

      if (!isCorrectInputData) {
        return;
      }

      await subscribe(
        isEmail
          ? {
              notificationChannel: BackInStockNotificationChannel.Email,
              itemCode: productCode,
              email: inputData,
            }
          : {
              notificationChannel: BackInStockNotificationChannel.Sms,
              itemCode: productCode,
              telephoneNumber: inputData,
            },
      );

      onSubmit();
    },
    [onSubmit, inputData, isCorrectInputData, isEmail, productCode, subscribe],
  );

  return (
    <Root data-testid={getTestId(NOTIFY_FORM)}>
      <Form>
        <Field>
          <Label isValid={isValid}>{label}</Label>
          <Input
            isInvalid={isInvalid}
            type="text"
            aria-label={label}
            value={inputData}
            onChange={onChange}
          />
          <Information isInvalid={isInvalid}>
            <ErrorCircle fontSize="inherit" />
          </Information>
          <Button
            type="submit"
            isInvalid={isInvalid}
            isValid={isValid}
            onClick={onClick}
          >
            {isCorrectInputData ? <SendFilled fontSize="inherit" /> : <Send fontSize="inherit" />}
          </Button>
        </Field>
        {!isCorrectInputData && <ValidateInfo>{errorLabel}</ValidateInfo>}
      </Form>
    </Root>
  );
};
